import { format } from "date-fns";

import { BackendResponseError, UserValidationError } from "./error-types";
import { getUserTokenIfSignedIn, isUserSignedIn, NotifyFailure } from "./auth/auth";
import { API_URL } from "./env-vars";

export enum LOG_IN_METHODS {
  SSO = "SSO",
  MSAL = "MSAL",
}

export enum LOG_IN_STATUS {
  SUCCESS = "success",
  FAILURE = "failure",
}

export enum LOGGED_IN {
  YES = "yes",
  NO = "no",
}

export enum CUSTOM_EVENTS {
  ERROR = "error",
  USER_LOG_IN = "userLogIn",
  USER_LOG_OUT = "userLogOut",
}

export const LOGO_SRC = `https://${window.location.host}/assets/logo/xls-logo.svg`;
export const dateFormat = "yyyy MMM dd, hh:mm a";

export function showMessage({ msg, todo = null, sheetName = null, address = null }) {
  const customEvent = new CustomEvent(CUSTOM_EVENTS.ERROR, {
    detail: {
      timestamp: format(Date.now(), dateFormat),
      msg: msg,
      todo: todo,
      sheetName: sheetName,
      address: address,
    },
  });
  window.dispatchEvent(customEvent);
}

export async function fetchFunctionResult(url: URL, invocation: CustomFunctions.Invocation) {
  try {
    let userToken: string = null;

    if (isUserSignedIn()) userToken = await getUserTokenIfSignedIn();

    if (!userToken) throw new UserValidationError("User is not signed in.", "Sign in to use custom functions");

    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: "Bearer " + userToken },
      cache: "no-cache",
    });

    if (!response.ok) {
      const error = await response.json();
      const msg = error.message;
      throw new BackendResponseError(msg);
    }

    return await response.json();
  } catch (error) {
    let todo: string = null;
    const msg = error.message;

    if (error instanceof UserValidationError) todo = error.todo;
    if (error instanceof BackendResponseError) todo = error.todo;

    const cellAddress = invocation.address;
    const [rawSheetName, address] = cellAddress.split("!");
    const sheetName = rawSheetName.replace(/^'/, "").replace(/'$/, "");

    NotifyFailure({ msg, todo, sheetName, address });

    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, error.message);
  }
}

export function buildAPIUrl(apiEndPoint: string, params: object = null) {
  const url = new URL(API_URL + apiEndPoint);

  if (params === null) return url;

  for (const param_key in params) {
    const param_value = params[param_key];

    // Remove all null/undefined parameters
    if (param_value === null || param_value === undefined) {
      continue;
    }

    // Build query parameters with array values
    if (param_value instanceof Array) {
      param_value.forEach((v: any) => {
        url.searchParams.append(param_key, v);
      });
      continue;
    }

    url.searchParams.append(param_key, param_value);
  }
  return url;
}

export async function selectCell(sheetName: string, address: string) {
  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    sheet.getRange(address).select();
    await context.sync();
  });
}

export async function insertCustomFunctionEgToCell(functionEg: string) {
  await Excel.run(async (context) => {
    try {
      const range = context.workbook.getSelectedRange();
      range.formulas = [[functionEg]];
    } catch (error) {
      showMessage({ msg: error.message, todo: "Select a single cell to insert the function example." });
    }
    await context.sync();
  });
}

/**
 * Rounds integer values in object to 2 decimal places
 */
export function roundNumbersTo2DP(__obj: Record<string, unknown>): Record<string, unknown> {
  const newKwargs = {};

  Object.entries(__obj).forEach(([key, value]) => {
    if (typeof value === "number") newKwargs[key] = parseFloat(value.toFixed(2));
    else newKwargs[key] = value;
  });

  return newKwargs;
}
